// src/Components/ProfessionalProfileForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  Autocomplete,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Badge as CertIcon,
  Build as ServiceIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  LinkedIn as LinkedInIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon
} from '@mui/icons-material';
import { industryCategories, serviceCategories } from '../schemas/userSchema';
import EmojiPicker from './EmojiPicker';

const ProfessionalProfileForm = ({
  professionalProfile = {},
  businessInfo = {},
  onSave,
  onCancel,
  loading = false
}) => {
  // Professional Profile State
  const [profile, setProfile] = useState({
    profession: '',
    jobTitle: '',
    company: '',
    industry: '',
    skills: [],
    services: [],
    certifications: [],
    yearsOfExperience: 0,
    workHistory: [],
    education: [],
    isAvailableForServices: false,
    hourlyRate: '',
    availability: '',
    serviceArea: '',
    ...professionalProfile
  });

  // Business Info State
  const [business, setBusiness] = useState({
    businessName: '',
    businessPhone: '',
    businessEmail: '',
    website: '',
    linkedIn: '',
    twitter: '',
    instagram: '',
    facebook: '',
    socialProfiles: [],
    portfolioUrl: '',
    businessDescription: '',
    showInDirectory: true,
    ...businessInfo
  });

  // Form state
  const [newSkill, setNewSkill] = useState('');
  const [newService, setNewService] = useState({ name: '', category: '', description: '' });
  const [newCertification, setNewCertification] = useState({ name: '', issuer: '', year: '' });
  const [newWorkEntry, setNewWorkEntry] = useState({ company: '', title: '', startYear: '', endYear: '', current: false });
  const [newEducation, setNewEducation] = useState({ institution: '', degree: '', field: '', year: '' });
  const [expanded, setExpanded] = useState('professional');

  useEffect(() => {
    if (professionalProfile) {
      setProfile(prev => ({ ...prev, ...professionalProfile }));
    }
    if (businessInfo) {
      setBusiness(prev => ({ ...prev, ...businessInfo }));
    }
  }, [professionalProfile, businessInfo]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Skills handlers
  const handleAddSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile({ ...profile, skills: [...profile.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setProfile({ ...profile, skills: profile.skills.filter(s => s !== skillToRemove) });
  };

  // Services handlers
  const handleAddService = () => {
    if (newService.name.trim()) {
      setProfile({
        ...profile,
        services: [...profile.services, { ...newService, id: Date.now() }]
      });
      setNewService({ name: '', category: '', description: '' });
    }
  };

  const handleRemoveService = (serviceId) => {
    setProfile({ ...profile, services: profile.services.filter(s => s.id !== serviceId) });
  };

  // Certification handlers
  const handleAddCertification = () => {
    if (newCertification.name.trim()) {
      setProfile({
        ...profile,
        certifications: [...profile.certifications, { ...newCertification, id: Date.now() }]
      });
      setNewCertification({ name: '', issuer: '', year: '' });
    }
  };

  const handleRemoveCertification = (certId) => {
    setProfile({ ...profile, certifications: profile.certifications.filter(c => c.id !== certId) });
  };

  // Work history handlers
  const handleAddWorkEntry = () => {
    if (newWorkEntry.company.trim() && newWorkEntry.title.trim()) {
      setProfile({
        ...profile,
        workHistory: [...profile.workHistory, { ...newWorkEntry, id: Date.now() }]
      });
      setNewWorkEntry({ company: '', title: '', startYear: '', endYear: '', current: false });
    }
  };

  const handleRemoveWorkEntry = (workId) => {
    setProfile({ ...profile, workHistory: profile.workHistory.filter(w => w.id !== workId) });
  };

  // Education handlers
  const handleAddEducation = () => {
    if (newEducation.institution.trim()) {
      setProfile({
        ...profile,
        education: [...profile.education, { ...newEducation, id: Date.now() }]
      });
      setNewEducation({ institution: '', degree: '', field: '', year: '' });
    }
  };

  const handleRemoveEducation = (eduId) => {
    setProfile({ ...profile, education: profile.education.filter(e => e.id !== eduId) });
  };

  const handleSave = () => {
    onSave({ professionalProfile: profile, businessInfo: business });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Professional Information */}
      <Accordion expanded={expanded === 'professional'} onChange={handleAccordionChange('professional')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon color="primary" />
            <Typography variant="h6">Professional Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Profession/Occupation"
                value={profile.profession}
                onChange={(e) => setProfile({ ...profile, profession: e.target.value })}
                placeholder="e.g., Software Engineer, Accountant, Teacher"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={profile.jobTitle}
                onChange={(e) => setProfile({ ...profile, jobTitle: e.target.value })}
                placeholder="e.g., Senior Developer, Partner, Principal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company/Employer"
                value={profile.company}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                placeholder="Current company name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={profile.industry}
                  label="Industry"
                  onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                >
                  {industryCategories.map((industry) => (
                    <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Years of Experience"
                value={profile.yearsOfExperience}
                onChange={(e) => setProfile({ ...profile, yearsOfExperience: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 70 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Area"
                value={profile.serviceArea}
                onChange={(e) => setProfile({ ...profile, serviceArea: e.target.value })}
                placeholder="e.g., Greater Atlanta Area, Remote/Nationwide"
              />
            </Grid>
          </Grid>

          {/* Skills Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Skills & Expertise
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Add a skill"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={handleAddSkill} startIcon={<AddIcon />}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {profile.skills.map((skill, index) => (
                <Chip
                  key={index}
                  label={skill}
                  onDelete={() => handleRemoveSkill(skill)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Services Offered */}
      <Accordion expanded={expanded === 'services'} onChange={handleAccordionChange('services')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ServiceIcon color="primary" />
            <Typography variant="h6">Services Offered to Family</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={profile.isAvailableForServices}
                onChange={(e) => setProfile({ ...profile, isAvailableForServices: e.target.checked })}
              />
            }
            label="I'm available to provide services to family members"
          />

          {profile.isAvailableForServices && (
            <>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hourly Rate (Optional)"
                    value={profile.hourlyRate}
                    onChange={(e) => setProfile({ ...profile, hourlyRate: e.target.value })}
                    placeholder="e.g., $50/hr, Free for family, Negotiable"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Availability"
                    value={profile.availability}
                    onChange={(e) => setProfile({ ...profile, availability: e.target.value })}
                    placeholder="e.g., Weekends only, Evenings after 6pm"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Services</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Service Name"
                      value={newService.name}
                      onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      size="small"
                      options={serviceCategories}
                      value={newService.category}
                      onChange={(e, value) => setNewService({ ...newService, category: value || '' })}
                      renderInput={(params) => <TextField {...params} label="Category" />}
                      freeSolo
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={newService.description}
                      onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmojiPicker
                              onEmojiSelect={(emoji) => setNewService({ ...newService, description: newService.description + emoji })}
                              size="small"
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="outlined"
                  onClick={handleAddService}
                  startIcon={<AddIcon />}
                  sx={{ mt: 1 }}
                  disabled={!newService.name.trim()}
                >
                  Add Service
                </Button>
              </Box>

              {profile.services.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {profile.services.map((service) => (
                    <Paper key={service.id} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle2">{service.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.category && `${service.category} • `}{service.description}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveService(service.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Certifications */}
      <Accordion expanded={expanded === 'certifications'} onChange={handleAccordionChange('certifications')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CertIcon color="primary" />
            <Typography variant="h6">Certifications & Licenses</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Certification Name"
                value={newCertification.name}
                onChange={(e) => setNewCertification({ ...newCertification, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Issuing Organization"
                value={newCertification.issuer}
                onChange={(e) => setNewCertification({ ...newCertification, issuer: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Year"
                value={newCertification.year}
                onChange={(e) => setNewCertification({ ...newCertification, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAddCertification}
                disabled={!newCertification.name.trim()}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {profile.certifications.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {profile.certifications.map((cert) => (
                <Paper key={cert.id} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">{cert.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cert.issuer}{cert.year && ` (${cert.year})`}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveCertification(cert.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Work History */}
      <Accordion expanded={expanded === 'workHistory'} onChange={handleAccordionChange('workHistory')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon color="primary" />
            <Typography variant="h6">Work History</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Company"
                value={newWorkEntry.company}
                onChange={(e) => setNewWorkEntry({ ...newWorkEntry, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={newWorkEntry.title}
                onChange={(e) => setNewWorkEntry({ ...newWorkEntry, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Year"
                value={newWorkEntry.startYear}
                onChange={(e) => setNewWorkEntry({ ...newWorkEntry, startYear: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="End Year"
                value={newWorkEntry.endYear}
                onChange={(e) => setNewWorkEntry({ ...newWorkEntry, endYear: e.target.value })}
                disabled={newWorkEntry.current}
                placeholder={newWorkEntry.current ? 'Present' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAddWorkEntry}
                disabled={!newWorkEntry.company.trim() || !newWorkEntry.title.trim()}
              >
                Add
              </Button>
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Switch
                checked={newWorkEntry.current}
                onChange={(e) => setNewWorkEntry({ ...newWorkEntry, current: e.target.checked, endYear: '' })}
                size="small"
              />
            }
            label="Current position"
          />

          {profile.workHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {profile.workHistory.map((work) => (
                <Paper key={work.id} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">{work.title} at {work.company}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {work.startYear} - {work.current ? 'Present' : work.endYear}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveWorkEntry(work.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Education */}
      <Accordion expanded={expanded === 'education'} onChange={handleAccordionChange('education')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon color="primary" />
            <Typography variant="h6">Education</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Institution"
                value={newEducation.institution}
                onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Degree"
                value={newEducation.degree}
                onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Field of Study"
                value={newEducation.field}
                onChange={(e) => setNewEducation({ ...newEducation, field: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Year"
                value={newEducation.year}
                onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAddEducation}
                disabled={!newEducation.institution.trim()}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {profile.education.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {profile.education.map((edu) => (
                <Paper key={edu.id} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">
                      {edu.degree ? `${edu.degree} in ${edu.field}` : edu.field || edu.institution}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {edu.institution}{edu.year && ` (${edu.year})`}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveEducation(edu.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Business Information */}
      <Accordion expanded={expanded === 'business'} onChange={handleAccordionChange('business')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">Business Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            Add your business details to appear in the Family Business Directory
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={business.showInDirectory}
                onChange={(e) => setBusiness({ ...business, showInDirectory: e.target.checked })}
              />
            }
            label="Show my profile in the Family Business Directory"
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={business.businessName}
                onChange={(e) => setBusiness({ ...business, businessName: e.target.value })}
                placeholder="Your business or company name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Phone"
                value={business.businessPhone}
                onChange={(e) => setBusiness({ ...business, businessPhone: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Email"
                type="email"
                value={business.businessEmail}
                onChange={(e) => setBusiness({ ...business, businessEmail: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                value={business.website}
                onChange={(e) => setBusiness({ ...business, website: e.target.value })}
                placeholder="https://yourwebsite.com"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><WebsiteIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="LinkedIn Profile"
                value={business.linkedIn}
                onChange={(e) => setBusiness({ ...business, linkedIn: e.target.value })}
                placeholder="https://linkedin.com/in/yourprofile"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LinkedInIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Twitter / X"
                value={business.twitter}
                onChange={(e) => setBusiness({ ...business, twitter: e.target.value })}
                placeholder="@handle"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Instagram"
                value={business.instagram}
                onChange={(e) => setBusiness({ ...business, instagram: e.target.value })}
                placeholder="@username"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Facebook"
                value={business.facebook}
                onChange={(e) => setBusiness({ ...business, facebook: e.target.value })}
                placeholder="facebook.com/business"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><FacebookIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Portfolio URL"
                value={business.portfolioUrl}
                onChange={(e) => setBusiness({ ...business, portfolioUrl: e.target.value })}
                placeholder="Link to your work samples"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Description"
                value={business.businessDescription}
                onChange={(e) => setBusiness({ ...business, businessDescription: e.target.value })}
                multiline
                rows={3}
                placeholder="Describe your business, what you offer, and what makes you unique..."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <EmojiPicker
                        onEmojiSelect={(emoji) => setBusiness({ ...business, businessDescription: business.businessDescription + emoji })}
                        size="small"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Professional Profile'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProfessionalProfileForm;
